import React, { useContext, useState, useEffect } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { TRANSPORT_MODES } from '../services/fbaInboundApi';
import { Truck, Package, Ship, Lock, CheckCircle, Loader2, AlertTriangle, X, RefreshCw } from 'lucide-react';

const Step5_TransportSelection = () => {
  const {
    setTransport,
    lockShipment,
    transportMode,
    shipmentLocked,
    selectedPlacementOption,
    nextStep,
    loading,
    statusMessage,
    markStepCompleted,
    goToStep
  } = useContext(FBAInboundContext);

  const [error, setError] = useState(null);
  const [isPlanCanceled, setIsPlanCanceled] = useState(false);

  // Mark step as completed when shipment is already locked
  useEffect(() => {
    if (shipmentLocked) {
      markStepCompleted(5);
    }
  }, [shipmentLocked, markStepCompleted]);

  const [selectedMode, setSelectedMode] = useState(transportMode || '');
  const [isSettingMode, setIsSettingMode] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [modeSet, setModeSet] = useState(!!transportMode);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  // Sync local state with context when transportMode changes (e.g., after page refresh restore)
  useEffect(() => {
    if (transportMode) {
      setSelectedMode(transportMode);
      setModeSet(true);
      console.log('Transport mode restored from context:', transportMode);
    }
  }, [transportMode]);

  const transportOptions = [
    {
      id: TRANSPORT_MODES.PARTNERED_SPD,
      label: 'Partnered Small Parcel',
      description: 'Use Amazon\'s partnered carrier for small parcel delivery. Best rates for small shipments.',
      icon: Package,
      recommended: true
    },
    {
      id: TRANSPORT_MODES.NON_PARTNERED_SPD,
      label: 'Non-Partnered Small Parcel',
      description: 'Use your own carrier for small parcel delivery. You\'ll need to provide tracking numbers.',
      icon: Truck,
      recommended: false
    },
    {
      id: TRANSPORT_MODES.NON_PARTNERED_LTL,
      label: 'Non-Partnered LTL',
      description: 'Use your own carrier for Less-Than-Truckload freight. Best for large pallet shipments.',
      icon: Ship,
      recommended: false
    }
  ];

  const handleSetMode = async () => {
    if (!selectedMode) return;

    setIsSettingMode(true);
    setError(null);
    try {
      await setTransport(selectedMode);
      setModeSet(true);
    } catch (err) {
      console.error('Failed to set transport mode:', err);
      setError(err.message);
    } finally {
      setIsSettingMode(false);
    }
  };

  const handleLockShipment = async () => {
    setIsLocking(true);
    setShowLockConfirm(false);
    setError(null);
    setIsPlanCanceled(false);

    try {
      await lockShipment();
      setTimeout(() => {
        nextStep();
      }, 1500);
    } catch (err) {
      console.error('Failed to lock shipment:', err);
      setError(err.message);
      // Check if this is a canceled plan error
      if (err.isPlanCanceled || err.message?.includes('canceled by Amazon') || err.message?.includes('cancelled inbound plan')) {
        setIsPlanCanceled(true);
      }
    } finally {
      setIsLocking(false);
    }
  };

  const handleGoToRecovery = () => {
    // Go back to Step 3 (Packing) where the auto-recover feature is
    goToStep(2);
  };

  // Success state
  if (shipmentLocked) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Shipment Locked
        </h3>
        <p className="text-gray-600">
          Your transport mode has been set and the shipment is now locked.
        </p>
        <div className="mt-4 inline-flex items-center px-3 py-1 bg-gray-100 rounded text-sm text-gray-700">
          <Lock className="w-4 h-4 mr-2" />
          {transportOptions.find(o => o.id === transportMode)?.label || transportMode}
        </div>
      </div>
    );
  }

  // Get shipment count from selected placement option
  const shipmentCount = selectedPlacementOption?.shipment_ids?.length || 1;

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Truck className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">Select Transport Mode</h4>
            <p className="text-sm text-blue-700 mt-1">
              Choose how your shipment{shipmentCount > 1 ? 's' : ''} will be transported to Amazon's fulfillment centers.
              {shipmentCount > 1 && ` This placement has ${shipmentCount} shipments.`}
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`border rounded-lg p-4 ${isPlanCanceled ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start">
            <AlertTriangle className={`w-5 h-5 mt-0.5 mr-3 ${isPlanCanceled ? 'text-amber-600' : 'text-red-600'}`} />
            <div className="flex-1">
              <h4 className={`font-medium ${isPlanCanceled ? 'text-amber-900' : 'text-red-900'}`}>
                {isPlanCanceled ? 'Plan Canceled by Amazon' : 'Error'}
              </h4>
              <p className={`text-sm mt-1 ${isPlanCanceled ? 'text-amber-700' : 'text-red-700'}`}>{error}</p>
              {isPlanCanceled && (
                <button
                  onClick={handleGoToRecovery}
                  className="mt-3 px-4 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 text-sm flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Go to Recovery (Step 3)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No placement option warning */}
      {!selectedPlacementOption && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-amber-900">No Placement Selected</h4>
              <p className="text-sm text-amber-700 mt-1">
                Please go back and select a placement option first.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Transport Options */}
      <div className="space-y-3">
        {transportOptions.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              onClick={() => {
                if (!modeSet) setSelectedMode(option.id);
              }}
              disabled={modeSet}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                selectedMode === option.id
                  ? 'border-blue-500 bg-blue-50'
                  : modeSet
                    ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start">
                <div className={`p-2 rounded-lg mr-4 ${
                  selectedMode === option.id ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    selectedMode === option.id ? 'text-blue-600' : 'text-gray-500'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">{option.label}</span>
                    {option.recommended && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                </div>
                {selectedMode === option.id && (
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Set Mode Button */}
      {!modeSet && (
        <button
          onClick={handleSetMode}
          disabled={!selectedMode || !selectedPlacementOption || isSettingMode || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSettingMode ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Setting Transport Mode...
            </>
          ) : (
            <>
              <Truck className="w-5 h-5 mr-2" />
              Set Transport Mode
            </>
          )}
        </button>
      )}

      {/* Lock Shipment Section */}
      {modeSet && !shipmentLocked && (
        <div className="border-t border-gray-200 pt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 mr-3" />
              <div>
                <h4 className="font-medium text-amber-900">Ready to Lock Shipment</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Locking the shipment confirms your transport selection and charges will apply.
                  This action cannot be undone easily.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowLockConfirm(true)}
            disabled={isLocking || loading}
            className="w-full py-3 px-4 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLocking ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {statusMessage || 'Locking...'}
              </>
            ) : (
              <>
                <Lock className="w-5 h-5 mr-2" />
                Lock Shipment & Confirm Charges
              </>
            )}
          </button>
        </div>
      )}

      {/* Lock Confirmation Modal */}
      {showLockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-md bg-white shadow-lg border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center text-amber-600">
                <Lock className="h-5 w-5 mr-2" />
                <h3 className="text-lg font-semibold">Confirm Lock Shipment</h3>
              </div>
              <button
                onClick={() => setShowLockConfirm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to lock this shipment?
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-4">
                <li>Partnered carrier charges will be applied</li>
                <li>You cannot change the transport mode after locking</li>
                <li>Box weights and dimensions cannot be modified</li>
              </ul>
              <p className="text-sm text-amber-600 font-medium">
                This action confirms charges and cannot be easily undone.
              </p>
            </div>

            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowLockConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLockShipment}
                disabled={isLocking}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center"
              >
                {isLocking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {statusMessage || 'Locking...'}
                  </>
                ) : (
                  'Confirm & Lock'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step5_TransportSelection;
